import { BehaviorSubject, Observable, catchError, map, of, retryWhen, throwError } from 'rxjs';
import {
  Checkout,
  ExpressCheckoutAddressType,
  IAddToExpressBuyNowCartRequest,
  ICreateExpressCheckoutResponse,
  IExpressCheckoutAddressRequest,
} from '@cart-checkout/data-access/interfaces';
import {
  CommerceAddress,
  ContactForm,
  Environment,
  InvalidEmailError,
} from '@shared/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { CookieService } from '../cookie.service';
import { Logger } from '../logger.service';
import { WINDOW } from '../window/window.service';
import { httpScalingRetryStrategy } from '../rxjs-operators/http-scaling-retry.operator';

@Injectable({
  providedIn: 'root',
})
export class ExpressCheckoutDao {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly environment = inject(Environment);
  private readonly cookieService = inject(CookieService);
  private readonly window = inject<Window>(WINDOW);
  private readonly invalidEmailErrorSubject = new BehaviorSubject<InvalidEmailError>(
    {} as InvalidEmailError,
  );

  public invalidEmailError$ = this.invalidEmailErrorSubject.asObservable();
  private readonly DEFAULT_HTTP_RETRIES = 3;
  private readonly DEFAULT_HTTP_RETRY_DURATION = 1000; // in milliseconds

  // Default HTTP Header properties
  private readonly DEFAULT_HTTP_HEADER_PROPERTIES = {
    Accept: MediaType.APPLICATION_JSON,
    'Content-Type': MediaType.APPLICATION_JSON,
    'Access-Control-Allow-Origin': '*',
    Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
    'x-api-version': '5',
    chk: '2',
    Referer: this.window?.location?.hostname,
  };

  /**
   * Adds a list of items to a new express cart for the "Buy Now" flow.
   * The AuthInterceptor will add the Authorization header
   * @param requestBody The list of items to add to the cart.
   * @returns An Observable that emits the `DCSG-EXPRESS-CART` cookie value on success, or null on failure.
   */
  public addToExpressBuyNowCart$(
    requestBody: IAddToExpressBuyNowCartRequest,
  ): Observable<string | null> {
    const url = `${this.environment.api.expressCart.basePath}/contents`;

    const headers = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);

    const httpOptions = {
      headers,
      withCredentials: true,
      observe: 'response' as const,
    };

    return this.http.put<null>(url, requestBody, httpOptions).pipe(
      retryWhen(
        httpScalingRetryStrategy({
          maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
          scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
          excludedStatusCodes: [],
          logger: this.logger,
        }),
      ),
      map((response: HttpResponse<null>) => {
        if (response.status === 200) {
          const cartCookie = this.cookieService.get('DCSG-EXPRESS-CART');
          if (!cartCookie) {
            this.logger.log(
              'ExpressCheckoutDao: "DCSG-EXPRESS-CART" header not found in response.',
            );
            return null;
          }
          this.logger.log('ExpressCheckoutDao: Successfully received express cart cookie.');
          return cartCookie;
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(
          'ExpressCheckoutDao: Error adding items to express cart after retries.',
          error,
        );
        return of(null);
      }),
    );
  }

  /**
   * Creates a new express checkout session.
   * This requires the DCSG-EXPRESS-CART cookie to be present on the request.
   * The AuthInterceptor will add the Authorization header
   * @returns An Observable containing the Checkout object and relevant cookies.
   */
  public createExpressCheckout$(): Observable<ICreateExpressCheckoutResponse | null> {
    const cookie = this.cookieService.get('DCSG-EXPRESS-CART');
    if (!cookie) {
      const errorMsg =
        'ExpressCheckoutDao: Cannot create express checkout, DCSG-EXPRESS-CART cookie is missing.';
      this.logger.error(errorMsg);
      return throwError(() => new Error(errorMsg));
    }

    const url = `${this.environment.api.expressCheckout.basePath}`;

    let headers = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);
    headers = headers.set('Cookie', `DCSG-EXPRESS-CART=${cookie}`);
    // Marks the request so ModernCheckoutInterceptor translates the modern (camelCase)
    // response body into the legacy Checkout shape (snake_case) consumed across the app.
    headers = headers.set(CustomHttpHeaders.X_CHK_MOD, 'true');

    const httpOptions = {
      headers,
      withCredentials: true,
      observe: 'response' as const,
    };

    return this.http.post<Checkout>(url, null, httpOptions).pipe(
      retryWhen(
        httpScalingRetryStrategy({
          maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
          scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
          excludedStatusCodes: [],
          logger: this.logger,
        }),
      ),
      map((response: HttpResponse<Checkout>) => {
        if (response.status === 201 && response.body) {
          const checkoutIdCookie = this.cookieService.get('X-DCSG-EXPRESS-CHECKOUT');
          const checkoutAccessKeyCookie = this.cookieService.get('DCSG-EXPRESS-CHECKOUT-ACCESS');

          this.logger.log('ExpressCheckoutDao: Successfully created express checkout session.');

          return {
            checkout: response.body,
            checkoutIdCookie,
            checkoutAccessKeyCookie,
          };
        }
        this.logger.error(`ExpressCheckoutDao: Received unexpected status code ${response.status}`);
        return null;
      }),
      catchError((error) => {
        this.logger.error(
          'ExpressCheckoutDao: Error creating express checkout session after retries.',
          error,
        );
        return of(null);
      }),
    );
  }

  /**
   * Submits a created express checkout session for processing.
   * The AuthInterceptor will add the Authorization header
   * This requires the DCSG-EXPRESS-CART and DCSG-EXPRESS-CHECKOUT-ACCESS cookies to be present.
   *
   * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
   * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
   *
   * @returns An Observable containing the updated Checkout object on success.
   */
  public submitExpressCheckout$(): Observable<Checkout | null> {
    const orderId = this.cookieService.get('X-DCSG-EXPRESS-CHECKOUT');
    if (!orderId) {
      const errorMsg =
        'ExpressCheckoutDao: Cannot submit checkout, orderId (checkoutKey) is missing.';
      this.logger.error(errorMsg);
      return throwError(() => new Error(errorMsg));
    }

    const expressCartCookie = this.cookieService.get('DCSG-EXPRESS-CART');
    const expressCheckoutAccessCookie = this.cookieService.get('DCSG-EXPRESS-CHECKOUT-ACCESS');
    if (!expressCartCookie || !expressCheckoutAccessCookie) {
      const errorMsg =
        'ExpressCheckoutDao: Cannot submit checkout, DCSG-EXPRESS-CART or DCSG-EXPRESS-CHECKOUT-ACCESS is missing.';
      this.logger.error(errorMsg);
      return throwError(() => new Error(errorMsg));
    }

    const url = `${this.environment.api.expressCheckout.basePath}/${orderId}/submit`;

    let headers = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);
    headers = headers.set(
      'Cookie',
      `DCSG-EXPRESS-CART=${expressCartCookie}; DCSG-EXPRESS-CHECKOUT-ACCESS=${expressCheckoutAccessCookie};`,
    );
    // Marks the request so ModernCheckoutInterceptor translates the modern (camelCase)
    // response body into the legacy Checkout shape (snake_case) consumed across the app.
    headers = headers.set(CustomHttpHeaders.X_CHK_MOD, 'true');

    const httpOptions = {
      headers,
      withCredentials: true,
    };

    return this.http.post<Checkout>(url, null, httpOptions).pipe(
      retryWhen(
        httpScalingRetryStrategy({
          maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
          scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
          excludedStatusCodes: [400, 410, 422, 426], // Status codes that should not be retried
          logger: this.logger,
        }),
      ),
      map((responseBody: Checkout) => {
        this.logger.log(`ExpressCheckoutDao: Successfully submitted express checkout ${orderId}.`);
        return responseBody;
      }),
      catchError((error) => {
        this.logger.error(
          `ExpressCheckoutDao: Error submitting express checkout ${orderId} after retries.`,
          error,
        );
        const errorMessage = error.error?.message ?? `Failed to submit order ${orderId}.`;
        return throwError(() => new Error(errorMessage));
      }),
    );
  }

  /**
   * Fetches the finalized (submitted) express checkout by orderId — called after a
   * successful submit so the confirmation view renders the server's submitted state
   * rather than the submit response. Mirrors the regular flow's
   * getSubmittedModernCheckout$; the X-CHK-MOD header opts the response into the same
   * modern→legacy translation as the other express APIs.
   * The AuthInterceptor will add the Authorization header.
   * This requires the DCSG-EXPRESS-CART and DCSG-EXPRESS-CHECKOUT-ACCESS cookies to be present.
   *
   * @returns An Observable containing the submitted Checkout object on success.
   */
  public getSubmittedExpressCheckout$(): Observable<Checkout | null> {
    const orderId = this.cookieService.get('X-DCSG-EXPRESS-CHECKOUT');
    if (!orderId) {
      const errorMsg =
        'ExpressCheckoutDao: Cannot fetch submitted checkout, orderId (checkoutKey) is missing.';
      this.logger.error(errorMsg);
      return throwError(() => new Error(errorMsg));
    }

    const expressCheckoutAccessCookie = this.cookieService.get('DCSG-EXPRESS-CHECKOUT-ACCESS');
    if (!expressCheckoutAccessCookie) {
      const errorMsg =
        'ExpressCheckoutDao: Cannot fetch submitted checkout, DCSG-EXPRESS-CHECKOUT-ACCESS is missing.';
      this.logger.error(errorMsg);
      return throwError(() => new Error(errorMsg));
    }

    const url = `${this.environment.api.expressCheckout.basePath}/submitted/${orderId}`;

    let headers = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);
    headers = headers.set('Cookie', `DCSG-EXPRESS-CHECKOUT-ACCESS=${expressCheckoutAccessCookie};`);
    headers = headers.set(CustomHttpHeaders.X_CHK_MOD, 'true');

    const httpOptions = {
      headers,
      withCredentials: true,
    };

    return this.http.get<Checkout>(url, httpOptions).pipe(
      retryWhen(
        httpScalingRetryStrategy({
          maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
          scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
          excludedStatusCodes: [400, 410, 422, 426],
          logger: this.logger,
        }),
      ),
      map((responseBody: Checkout) => {
        this.logger.log(
          `ExpressCheckoutDao: Successfully fetched submitted express checkout ${orderId}.`,
        );
        return responseBody;
      }),
      catchError((error) => {
        this.logger.error(
          `ExpressCheckoutDao: Error fetching submitted express checkout ${orderId} after retries.`,
          error,
        );
        const errorMessage = error.error?.message ?? `Failed to fetch submitted order ${orderId}.`;
        return throwError(() => new Error(errorMessage));
      }),
    );
  }

  /**
   * Adds or updates an address on an express checkout session.
   * The AuthInterceptor will add the Authorization header.
   *
   * @param contact The contact information to add/update.
   * @param address The address information to add/update.
   * @param addressType The type of address (defaults to SHIPPINGBILLING).
   * @returns An Observable containing the updated Checkout object on success.
   */
  public addExpressAddress$(
    contact: ContactForm,
    address: CommerceAddress,
    addressType: ExpressCheckoutAddressType = ExpressCheckoutAddressType.SHIPPING_BILLING,
  ): Observable<Checkout | null> {
    const orderId = this.cookieService.get('X-DCSG-EXPRESS-CHECKOUT');
    const url = `${this.environment.api.expressCheckout.basePath}/${orderId}/address`;

    let headers = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);
    // Marks the request so ModernCheckoutInterceptor translates the modern (camelCase)
    // response body into the legacy Checkout shape (snake_case) consumed across the app.
    headers = headers.set(CustomHttpHeaders.X_CHK_MOD, 'true');

    const params = new HttpParams().set('type', addressType);

    const httpOptions = {
      headers,
      params,
      withCredentials: true,
    };

    const bodyPayload = this.toAddressRequest(contact, address);

    return this.http.post<Checkout>(url, bodyPayload, httpOptions).pipe(
      retryWhen(
        httpScalingRetryStrategy({
          maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
          scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
          excludedStatusCodes: [400], // Exclude 400 so the email validation logic in catchError can run immediately
          logger: this.logger,
        }),
      ),
      map((responseBody: Checkout) => {
        this.logger.log(
          `ExpressCheckoutDao: Successfully updated address for express checkout ${orderId}.`,
        );
        return responseBody;
      }),
      catchError((error) => {
        this.logger.error(
          `ExpressCheckoutDao: Error updating address for express checkout ${orderId}.`,
          error,
        );
        if (this.isInvalidEmailError(error)) {
          this.handleInvalidEmailError(error);
        }
        const errorMessage =
          error.error?.message ?? `Failed to update address for order ${orderId}.`;
        return throwError(() => new Error(errorMessage));
      }),
    );
  }

  /**
   * Applies a promotion or reward code to an express checkout session.
   * The AuthInterceptor will add the Authorization header.
   *
   * @param checkoutKey The express checkout order id.
   * @param promoOrRewardCode The promotion or reward code to apply.
   * @returns An Observable containing the updated Checkout object on success.
   */
  public applyExpressPromotionOrRewardCode$(
    checkoutKey: string,
    promoOrRewardCode: string,
  ): Observable<Checkout> {
    const url = `${this.environment.api.expressCheckout.basePath}/${checkoutKey}/promotion/cart`;
    return this.http.put<Checkout>(
      url,
      { promotionCode: promoOrRewardCode },
      this.buildPromotionHttpOptions(),
    );
  }

  /**
   * Removes a promotion or reward code from an express checkout session.
   * The AuthInterceptor will add the Authorization header.
   *
   * @param checkoutKey The express checkout order id.
   * @param promoOrRewardCode The promotion or reward code to remove.
   * @returns An Observable containing the updated Checkout object on success.
   */
  public removeExpressPromotionOrRewardCode$(
    checkoutKey: string,
    promoOrRewardCode: string,
  ): Observable<Checkout> {
    const url = `${this.environment.api.expressCheckout.basePath}/${checkoutKey}/promotion/cart`;

    return this.http.delete<Checkout>(url, {
      ...this.buildPromotionHttpOptions(),
      body: { promotionCode: promoOrRewardCode },
    });
  }

  toAddressRequest(contact: ContactForm, address: CommerceAddress): IExpressCheckoutAddressRequest {
    return {
      firstName: address?.first_name,
      lastName: address?.last_name,
      address: address?.address,
      address2: address?.address2,
      zipcode: address?.zipcode,
      city: address?.city,
      state: address?.state,
      country: address?.country ?? 'US',
      phone: contact.phone ?? '',
      email: contact.email ?? '',
    };
  }

  private buildPromotionHttpOptions(): { headers: HttpHeaders; withCredentials: boolean } {
    let headers = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);
    headers = headers.set(CustomHttpHeaders.X_CHK_MOD, 'true');

    return {
      headers,
      withCredentials: true,
    };
  }

  private isInvalidEmailError(error: HttpErrorResponse): boolean {
    const payload = error.error;
    return (
      error.status === 400 &&
      // eslint-disable-next-line no-prototype-builtins
      payload?.hasOwnProperty('ui_messaging') &&
      payload['ui_messaging'].identifier === 'DCSG-CHECKOUT-INVALID-EMAIL'
    );
  }

  private set invalidEmailError(error: InvalidEmailError) {
    this.invalidEmailErrorSubject.next(error);
  }

  private handleInvalidEmailError(error: HttpErrorResponse): void {
    this.invalidEmailError = error?.error;
  }
}
